/** 計分與段位。前端與 Worker 共用：伺服器以同一公式重算，排行榜以伺服器結果為準。 */
import type { QType, Tier } from "./types";

export const LIMIT_MS = 20000; // 每題限時
export const RUN_SIZE = 10; // 每局題數
export const TIER_BASE: Record<Tier, number> = { N: 10, R: 14, SR: 18, SSR: 24 };
export const TYPE_BONUS: Record<QType, number> = { gloss: 0, usage: 2, exam: 4, pair: 6, same: 6 };

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** 單題得分：基礎分（依稀有度＋題型）＋速度加成（最多 +50%）× 連擊倍率（最多 ×1.9）。答錯 0 分。 */
export function questionScore(args: { correct: boolean; tier: Tier; type: QType; ms: number; combo: number }): number {
  if (!args.correct) return 0;
  const base = TIER_BASE[args.tier] + TYPE_BONUS[args.type];
  const ms = args.ms < 300 ? LIMIT_MS : args.ms; // 不足 0.3 秒視為異常，不給速度分
  const speed = clamp(1 - ms / LIMIT_MS, 0, 1);
  const bonus = Math.round(base * 0.5 * speed);
  const mult = 1 + clamp(args.combo - 1, 0, 9) * 0.1;
  return Math.round((base + bonus) * mult);
}

export interface RankDef {
  name: string;
  glyph: string;
  tone: string;
  /** 詞力門檻（詞力＝對局入帳＋成就加成，見 prestigeOf） */
  xp: number;
  /** 最高階：另須題庫裡每一題都答對過 */
  all?: boolean;
}

/** 段位的四項原料（對局入帳詞力、收服、三星通關、全通域）＋題海（答對過一次的題數／題庫總題數） */
export interface RankStats {
  xp: number;
  mastered: number;
  cleared: number;
  regions: number;
  done?: number;
  total?: number;
  /** 答對過的退役題（2026-11-03 題庫修正後不再出的題）：仍計題海加成，不計完成度與文聖 */
  retired?: number;
}

/** 成就加成（2026-10-31 v2.10，站長定案「成就併入詞力」：排行與段位要一條尺）：
 * 收服每隻 +30、三星通關每關 +120、全通一域 +1,500、題海每題 +4。 */
export const BONUS = { mastered: 30, cleared: 120, regions: 1500, done: 4 } as const;

export function bonusOf(s: RankStats): number {
  return s.mastered * BONUS.mastered + s.cleared * BONUS.cleared + s.regions * BONUS.regions + ((s.done || 0) + (s.retired || 0)) * BONUS.done;
}

/** 詞力＝對局入帳（xp）＋成就加成。段位門檻、首頁與我的頁的詞力、總榜都看這個數。 */
export function prestigeOf(s: RankStats): number {
  return s.xp + bonusOf(s);
}

/** 科舉段位（2026-10-31 v2.10）：只看詞力門檻，門檻依當日 34 位線上玩家校準，無人降階（reports/operations/wygame-v210-20261031/calibrate-v210.txt）；
 * 文聖另須題庫裡每一題都答對過（站長：做完做對全部題目的人給最高等級）。此前的四項門檻（收服／通關／全通域）改為加成，見 docs/RANK_DESIGN.md。 */
export const RANKS: RankDef[] = [
  { name: "童生", glyph: "童", tone: "#9aa5a1", xp: 0 },
  { name: "秀才", glyph: "秀", tone: "#5aa9e6", xp: 600 },
  { name: "舉人", glyph: "舉", tone: "#4fb08a", xp: 1800 },
  { name: "貢士", glyph: "貢", tone: "#b48ef1", xp: 5000 },
  { name: "進士", glyph: "進", tone: "#e6c36b", xp: 12000 },
  { name: "翰林", glyph: "翰", tone: "#f0a35e", xp: 20000 },
  { name: "狀元", glyph: "狀", tone: "#ff6b4a", xp: 32000 },
  { name: "文曲", glyph: "曲", tone: "#ffd36b", xp: 48000 },
  { name: "文昌", glyph: "昌", tone: "#8fd3ff", xp: 65000 },
  { name: "魁星", glyph: "魁", tone: "#c9a7ff", xp: 85000 },
  { name: "紫微", glyph: "紫", tone: "#ff9ad5", xp: 110000 },
  { name: "文聖", glyph: "聖", tone: "#fff1b0", xp: 110000, all: true },
];

/** 文曲的索引（之上是題海四階） */
export const WENQU_IDX = 7;

export type RankKey = "xp" | "done";
export const RANK_KEYS: RankKey[] = ["xp", "done"];

export interface RankNeed {
  key: RankKey;
  have: number;
  need: number;
  left: number;
}

export interface RankInfo {
  idx: number;
  name: string;
  glyph: string;
  tone: string;
  stars: number; // 0..3 within the rank
  progress: number; // 0..1 to next rank
  next: RankDef | null;
  toNext: number; // 還差的詞力
  needs: RankNeed[]; // 下一階還沒達到的門檻
  prestige: number; // 詞力（對局入帳＋成就加成）
}

/** 某一階是否達到：詞力門檻；最高階另須全部題目答對過（題庫總題數未知時視為達不到） */
export function meetsRank(s: RankStats, r: RankDef): boolean {
  if (prestigeOf(s) < r.xp) return false;
  if (r.all) return !!s.total && s.total > 0 && (s.done || 0) >= s.total;
  return true;
}

export function emptyStats(xp = 0): RankStats {
  return { xp, mastered: 0, cleared: 0, regions: 0, done: 0, total: 0 };
}

/** 段位索引：從低到高連續達標的最高一階。prestige 已含成就加成；done／total 只決定文聖。 */
export function rankIdxOf(prestige: number, done = 0, total = 0): number {
  let idx = 0;
  for (let i = 0; i < RANKS.length; i++) {
    const r = RANKS[i];
    const ok = prestige >= r.xp && (!r.all || (total > 0 && done >= total));
    if (ok) idx = i;
    else break;
  }
  return idx;
}

export function rankOf(stats: RankStats): RankInfo {
  const prestige = prestigeOf(stats);
  const idx = rankIdxOf(prestige, stats.done || 0, stats.total || 0);
  const cur = RANKS[idx];
  const next = RANKS[idx + 1] || null;
  const needs: RankNeed[] = [];
  let progress = 1;
  if (next) {
    progress = next.xp <= cur.xp ? 1 : clamp((prestige - cur.xp) / (next.xp - cur.xp), 0, 1);
    if (prestige < next.xp) needs.push({ key: "xp", have: prestige, need: next.xp, left: next.xp - prestige });
    if (next.all) {
      const total = stats.total || 0;
      const done = stats.done || 0;
      if (total > 0) {
        progress = Math.min(progress, clamp(done / total, 0, 1));
        if (done < total) needs.push({ key: "done", have: done, need: total, left: total - done });
      } else progress = 0;
    }
  }
  return { idx, name: cur.name, glyph: cur.glyph, tone: cur.tone, stars: next ? Math.min(3, Math.floor(progress * 3) + 1) : 3, progress, next, toNext: next ? Math.max(0, next.xp - prestige) : 0, needs, prestige };
}

export const NEED_LABEL: Record<RankKey, (n: number) => string> = {
  xp: (n) => `再 ${n} 詞力`,
  done: (n) => `再答對 ${n} 題`,
};
